'use client';

import React, { useState } from 'react';
import {
  Avatar,
  Chip,
  Link,
  Popover,
  PopoverTrigger,
  PopoverContent,
  Snippet,
  Tooltip,
} from '@heroui/react';
import { useTranslations } from '../../../../contexts/translation.context';
import { RevshareCampaign } from '../types';
import { Icon } from '@iconify/react';
import {
  LinkIcon,
  EyeIc,
  OnlyFansIc,
  InstagramIc,
  TwitterIc,
  GoogleIc,
  IcReddit,
  IcTikTok,
  IcTg,
  IcYoutube,
  IcAnotherSoc,
  GlobalIc,
  ChatRoundedIc,
} from '../../../../components/icons';
import BlockedCountriesTooltip from '../../../subscribers/orders/components/BlockedCountriesTooltip';
import { ResponsiveTooltip } from '../../../../components';

interface CampaignCellProps {
  campaign: RevshareCampaign;
}

// Get source icon - same as in orders
const getSourceIcon = (source: string, size: number = 16) => {
  const iconMap: Record<string, React.ReactElement> = {
    only_fans: <OnlyFansIc size={size} fill="#A8A8B0" />,
    reddit: <IcReddit size={size} />,
    instagram: <InstagramIc size={size} fill="#A8A8B0" />,
    tiktok: <IcTikTok size={size} />,
    google: <GoogleIc size={size} fill="#A8A8B0" />,
    telegram: <IcTg size={size} />,
    twitter: <TwitterIc size={size} fill="#A8A8B0" />,
    youtube: <IcYoutube size={size} />,
    another: <IcAnotherSoc size={size} />,
  };

  return iconMap[source?.toLowerCase()] || <GlobalIc size={size} />;
};

// Get source label - same as in orders
const getSourceLabel = (source: string, t: any) => {
  const labelMap: Record<string, string> = {
    only_fans: t('proposals.onlyFans'),
    reddit: t('proposals.reddit'),
    instagram: t('proposals.instagram'),
    tiktok: t('proposals.tiktok'),
    google: t('proposals.google'),
    telegram: t('proposals.telegram'),
    twitter: t('proposals.twitter'),
    youtube: t('proposals.youtube'),
    another: t('proposals.otherOne'),
  };

  return labelMap[source?.toLowerCase()] || source;
};

const CampaignCell: React.FC<CampaignCellProps> = ({ campaign }) => {
  const { t } = useTranslations();
  const [isBlockedCountriesOpen, setIsBlockedCountriesOpen] = useState(false);

  // Get model data
  const model = campaign.model || {};
  const modelAvatar = model.avatar;
  // Shorten model ID to 4 characters (ON2519 instead of ON251936121)
  const fullTrackLinkId = model.of_user_id ? String(model.of_user_id) : '';
  const shortId = fullTrackLinkId.length > 4 ? fullTrackLinkId.slice(-4) : fullTrackLinkId;
  const modelCode = shortId ? `${shortId}` : '';
  const modelUsername = model.of_user_name || '';
  const modelLink = model.link || '';
  const blockedCountries = model.blocked_countries || [];
  const topPerformer = model.overall_popularity_percent || 0;

  // Get offer data
  const offer = campaign.offer || {};
  const source = campaign.source || '';
  const commission = offer.commission || 0;
  const freeTrialAccess = offer.free_trial_access || false;

  // Get owner (creator/agency) name from offer.client
  const ownerName = offer.client?.name || '';

  // Get model stats from of_data
  const ofData = model.of_data || {};
  const subscribers = ofData.subscribers || 0;
  const favorited = ofData.favorited || 0;
  const posts = ofData.posts || 0;

  // Get campaign name and note
  const campaignName = campaign.name || '';
  const campaignNote = campaign.description || '';

  // Get clicks and calculate conversion percentage
  const clicks = model.transitions_count || 0;
  const subscribedCount = campaign.subscribed_count || 0;
  const clicksPercent = clicks > 0 ? ((subscribedCount / clicks) * 100).toFixed(0) : '0';

  // Determine traffic type
  const trafficType = freeTrialAccess ? 'Free-Trial' : 'По ссылке';

  // Determine subscription type
  //@ts-ignore
  const subscriptionType =
    campaign.type === 'free'
      ? 'FREE'
      : campaign.type === 'free_trial' || campaign.type === 'free-trial' || campaign.type === 'paid' || campaign.type === 'free trial'
        ? 'Free-Trial'
        : 'FREE';

  const openBlockedCountries = () => setIsBlockedCountriesOpen(true);
  const scheduleBlockedCountriesClose = () => {
    setTimeout(() => setIsBlockedCountriesOpen(false), 200);
  };

  return (
    <div className="flex items-start gap-4 py-2">
      {/* Model Avatar */}
      <Avatar
        src={modelAvatar}
        alt={model.name || modelUsername}
        className="w-8 h-8 shrink-0 rounded-2"
        radius="sm"
      />

      <div className="flex flex-col gap-1">
        {/* Row 1: Model code + info tooltip + owner name (creator/agency) + TOP performer + subscription type */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium text-auth-ink leading-5">{modelCode}</span>

          {/* Model info tooltip (subscribers, likes, posts)
          <ResponsiveTooltip
            content={
              <div className="flex flex-col gap-1 p-1">
                <div className="flex items-center gap-2">
                  <Icon icon="solar:users-group-two-rounded-linear" width={14} className="text-auth-muted" />
                  <span className="text-xs">{t('partner.revshare.campaigns.subscribers') || 'Подписчиков'}: {subscribers.toLocaleString()}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Icon icon="solar:heart-linear" width={14} className="text-auth-muted" />
                  <span className="text-xs">{t('partner.revshare.campaigns.likes') || 'Лайков'}: {favorited.toLocaleString()}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Icon icon="solar:gallery-linear" width={14} className="text-auth-muted" />
                  <span className="text-xs">{t('partner.revshare.campaigns.posts') || 'Постов'}: {posts.toLocaleString()}</span>
                </div>
              </div>
            }
            placement="top"
          >
            <div className="cursor-pointer">
              <Icon icon="solar:info-circle-bold" width={16} className="text-neutral-8" />
            </div>
          </ResponsiveTooltip> */}

          {/* OnlyFans username link */}
          {modelUsername && (
            <Link
              href={`https://onlyfans.com/${modelUsername}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-partner"
            >
              @{modelUsername}
            </Link>
          )}

          {/* Top Performer Badge (warning/orange color) */}
          {topPerformer > 0 && (
            <ResponsiveTooltip content={t('partner.partnerCampaigns.topCreatorsTooltip')} placement="top">
              <Chip
                variant="flat"
                size="sm"
                className="bg-[rgba(245,165,36,0.2)] text-warning rounded-full px-1 h-6"
              >
                <span className="text-xs leading-4">{topPerformer}%</span>
              </Chip>
            </ResponsiveTooltip>
          )}

          {/* Subscription Type Badge (primary/blue color) */}
          <ResponsiveTooltip content={t('partner.partnerCampaigns.subscriptionTypeTooltip')} placement="top">
            <Chip
              variant="flat"
              size="sm"
              className="bg-[rgba(0,111,238,0.2)] text-partner rounded-full px-1 h-6"
            >
              <span className="text-xs leading-4">{subscriptionType}</span>
            </Chip>
          </ResponsiveTooltip>
        </div>

        {/* Row 2: Source date + Campaign name + Source badge + traffic type */}
        <div className="flex items-center gap-2 flex-wrap">
          {/* Source + Date + Campaign Name badge (purple/secondary color) */}
          {source && campaign.created_at && (
            <Chip
              variant="flat"
              size="sm"
              className="bg-[rgba(120,40,200,0.2)] text-accent rounded-full px-2 h-6"
            >
              <span className="text-xs leading-4">
                {campaignName ? ` ${campaignName}` : getSourceLabel(source, t)} -{' '}
                {new Date(campaign.created_at).toLocaleDateString('ru-RU', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                })}
              </span>
            </Chip>
          )}

          {/* Source icon badge */}
          {source && (
            <Chip
              variant="bordered"
              size="sm"
              className="border-auth-strength-none border-[1.4px] rounded-3.5 px-1 h-6 gap-0.5"
              startContent={
                <div className="w-3.5 h-3.5 flex items-center justify-center">
                  {getSourceIcon(source, 14)}
                </div>
              }
            >
              <span className="text-xs text-auth-ink leading-4">{getSourceLabel(source, t)}</span>
            </Chip>
          )}

          {/* <span className="text-xs font-semibold text-auth-ink leading-4">{trafficType}</span> */}
        </div>

        {/* Row 2: Link with eye icon and clicks count + conversion */}
        {modelLink && (
          <div className="flex items-center gap-1">
            <Snippet
              symbol={null}
              codeString={modelLink}
              className="bg-husky rounded-full px-2 py-[0.1875rem]"
              classNames={{
                base: 'bg-husky rounded-full px-2 py-[0.1875rem] flex items-center gap-1 shadow-none',
                pre: 'bg-transparent p-0 flex items-center gap-1 max-w-[13.75rem]',
                copyButton: 'text-partner min-w-5 h-5',
                checkIcon: 'text-partner',
                content: 'flex items-center gap-1 max-w-[13.75rem]',
              }}
            >
              <a
                href={modelLink}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 no-underline"
              >
                <LinkIcon size={18} fill="#006FEE" className="text-partner" />
                <span className="text-sm text-partner truncate max-w-[11.25rem]">{modelLink}</span>
              </a>
            </Snippet>
            <div className="flex items-center gap-1 ml-1">
              <ResponsiveTooltip content={t('partner.partnerCampaigns.guestsSubscribersTooltip')} placement="top">
                <div className="flex items-center gap-1">
                  <EyeIc size={16} className="text-neutral-8" />
                  <span className="text-neutral-8 text-xs font-medium leading-4">{clicks}</span>
                  <span className="text-neutral-8 text-xs font-normal leading-4">
                    ({clicksPercent}%)
                  </span>
                </div>
              </ResponsiveTooltip>
            </div>
          </div>
        )}

        {/* Row 3: Blocked countries */}
        {blockedCountries.length > 0 && (
          <Popover
            placement="bottom-start"
            offset={12}
            showArrow
            isOpen={isBlockedCountriesOpen}
            onOpenChange={setIsBlockedCountriesOpen}
            shouldCloseOnBlur
            isDismissable
          >
            <PopoverTrigger>
              <div
                className="cursor-pointer"
                onMouseEnter={openBlockedCountries}
                onMouseLeave={scheduleBlockedCountriesClose}
              >
                <span className="text-xs text-auth-muted leading-[1.125rem]">
                  {t('partner.orders.blockedCountries') || 'Заблокированные страны'}:{' '}
                  {blockedCountries.length}
                </span>
              </div>
            </PopoverTrigger>
            <PopoverContent
              className="p-0 bg-white border border-tin rounded-2xl shadow-xl"
              onMouseEnter={openBlockedCountries}
              onMouseLeave={scheduleBlockedCountriesClose}
            >
              <BlockedCountriesTooltip
                countries={blockedCountries}
                offerShortId={modelCode}
                modelUsername={modelUsername}
                profileUrl={`https://onlyfans.com/${modelUsername}`}
              />
            </PopoverContent>
          </Popover>
        )}

        {/* Row 4: Note */}
        {campaignNote && (
          <ResponsiveTooltip content={campaignNote} placement="top" className="max-w-[18.75rem]">
            <div className="flex items-center gap-1 cursor-pointer">
              <ChatRoundedIc size={14} className="text-neutral-8" />
              <span className="text-xs text-auth-muted leading-4">
                {t('partner.partnerCampaigns.note') || 'Примечание'}
              </span>
            </div>
          </ResponsiveTooltip>
        )}
      </div>
    </div>
  );
};

export default CampaignCell;
